import axios from "axios";
import { Dispatch } from "redux";
import { httpClient } from "../../clients/auth/addTokenToHTTPRequests";
import { fetchAllProducts, fetchHomepageProducts, fetchProductsByCollectionHandle, registerActiveSearchChecker, searchProducts } from "../../clients/shopify/shopifyClient";
import { transformShopifyProductsToItemDataArray } from "../../clients/shopify/shopifyTransformer";
import { ACTION_TYPES, PaginationInfo } from "../../types/shopify";
import { MAX_EMPTY_HALF_PRICE_BATCHES } from '../../constants/paginationConstants';
import { sortItems as applySortToItems } from "../../utils/sortUtils";

export const sortItems = (
  sortType: "A-Z" | "Z-A" | "HighestPrice" | "LowestPrice" | "Difference"
) => {
  return {
    type: ACTION_TYPES.SORT_ITEMS,
    sortType,
  };
};

export const filterItems = (filter: FilterOption) => {
  return {
    type: ACTION_TYPES.FILTER_ITEMS,
    filter,
  };
};

export const removeFilter = (filter: FilterOption) => {
  return {
    type: ACTION_TYPES.REMOVE_FILTER,
    filter,
  };
};

export const toggleUnavailableProducts = (
  isUnavailableProductsHidden: boolean
) => {
  return {
    type: ACTION_TYPES.TOGGLE_UNAVAILABLE_PRODUCTS,
    isUnavailableProductsHidden,
  };
};

export const sortItemsOnApi =
  (
    sortType:
      | ""
      | "A-Z"
      | "Z-A"
      | "HighestPrice"
      | "LowestPrice"
      | "Difference",
    categoryFilterId?: number,
    searchTerm?: string
  ) =>
  async (dispatch: Dispatch<any>, getState: () => any) => {
    console.log(`Applying client-side sorting: ${sortType}`);
    
    // Map the sort type to a user-friendly name
    let sortTypeName = sortType === "" ? "" : `Name (${sortType})`;
    if (sortType === "HighestPrice" || sortType === "LowestPrice") {
      sortTypeName = sortType === "HighestPrice"
        ? "Price (High to Low)"
        : "Price (Low to High)";
    }
    if (sortType === "Difference") {
      sortTypeName = "Biggest Savings";
    }
    
    // Get current state to access the items
    const state = getState();
    const currentItems = state.items?.itemsToDisplay || [];
    
    // If we already have items in the store, sort them client-side
    if (currentItems.length > 0) {
      console.log(`Sorting ${currentItems.length} existing items client-side`);
      
      // Apply sorting to a copy of the items
      const sortedItems = applySortToItems(currentItems, sortType);
      
      // Update the Redux store with the sorted items
      dispatch({
        type: ACTION_TYPES.DISPLAY_ITEMS,
        itemsToDisplay: sortedItems,
        // Preserve existing pagination info
        pageNumber: state.items.currentPageNumber,
        totalPages: state.items.totalPages,
        totalProductsFound: state.items.totalProductsFound,
        sortType: sortType,
        sortTypeName: sortTypeName,
        // Preserve existing match data
        matchedItem: state.items.matchedItem || [],
        closeMatchId: state.items.closeMatchId || 1,
        currentItemId: state.items.currentItemId || 1,
        // Preserve pagination info
        paginationInfo: state.items.paginationInfo
      });
    } else {
      // If we don't have any items yet, we need to fetch them from the API
      console.log('No existing items to sort, fetching from API...');
      
      // Fetch items with the selected search/category but apply default sort (A-Z)
      // We'll sort the results client-side after fetching
      dispatch(getItemsToDisplay(
        searchTerm || '',
        categoryFilterId || 0,
        state.items.appliedFilters || [],
        sortType, // Pass the sort type to getItemsToDisplay
        state.items.isHalfThePriceClicked || false
      ));
    }
  };

export const toggleHalfThePriceProducts =
  (
    isHalfThePriceClicked: boolean,
    callTheAPI: boolean = true,
    categoryFilterId?: number
  ) =>
  async (dispatch: Dispatch<any>, getState: () => any) => {
    console.log(`Toggling half price filter: ${isHalfThePriceClicked}`);    

    // Get current state to access all items
    const state = getState();
    const currentItems = state.items?.itemsToDisplay || [];
    const currentSortType = state.items?.sortType || "A-Z";
    const currentSortTypeName = state.items?.sortTypeName || "Name (A-Z)";
    
    // First, check what state we're transitioning to
    if (isHalfThePriceClicked) {
      // TURNING FILTER ON - First make sure the store has a snapshot of ALL products
      console.log('Turning 1/2 price filter ON');
      console.log(`Current items count: ${currentItems.length}`);
      
      // Make sure we update the full set of items for later restoration
      dispatch({
        type: ACTION_TYPES.SAVE_ORIGINAL_ITEMS,
        originalItems: [...currentItems], // Deep copy to avoid reference issues
      });
      
      // Now filter to show only half-price items
      const halfPriceItems = currentItems.filter(item => item.isHalfThePrice);
      console.log(`Filtered down to ${halfPriceItems.length} half-price items`);
      
      // Apply sorting to maintain consistent order
      const sortedItems = applySortToItems(halfPriceItems, currentSortType);
      
      // Update display to show only half-price items
      dispatch({
        type: ACTION_TYPES.DISPLAY_ITEMS,
        itemsToDisplay: sortedItems,
        // Preserve existing pagination and metadata
        pageNumber: state.items.currentPageNumber,
        totalPages: state.items.totalPages,
        totalProductsFound: sortedItems.length,
        sortType: currentSortType,
        sortTypeName: currentSortTypeName,
        matchedItem: state.items.matchedItem || [],
        closeMatchId: state.items.closeMatchId || 1,
        currentItemId: state.items.currentItemId || 1,
        paginationInfo: state.items.paginationInfo
      });
      
    } else {
      // TURNING FILTER OFF - Restore original items from before filtering
      console.log('Turning 1/2 price filter OFF');
      
      // Get the saved original items
      const savedOriginalItems = state.items?.originalItemsBeforeHalfPrice || [];
      console.log(`Saved original items count: ${savedOriginalItems.length}`);
      
      if (savedOriginalItems.length > 0) {
        // We have saved items to restore
        console.log(`Restoring ${savedOriginalItems.length} original items`);
        
        // Apply sorting to maintain consistent order
        const sortedItems = applySortToItems(savedOriginalItems, currentSortType);
        
        // Restore the original items
        dispatch({
          type: ACTION_TYPES.DISPLAY_ITEMS,
          itemsToDisplay: sortedItems,
          // Preserve existing pagination and metadata  
          pageNumber: state.items.currentPageNumber,
          totalPages: state.items.totalPages,
          totalProductsFound: sortedItems.length,
          sortType: currentSortType,
          sortTypeName: currentSortTypeName,
          matchedItem: state.items.matchedItem || [],
          closeMatchId: state.items.closeMatchId || 1,
          currentItemId: state.items.currentItemId || 1,
          paginationInfo: state.items.paginationInfo
        });
      } else if (callTheAPI) {
        // Fallback if no saved items - fetch fresh from API
        console.log('No saved original items, fetching from API');
        
        dispatch(getItemsToDisplay(
          "", // Empty search term
          categoryFilterId || 0, 
          state.items.appliedFilters || [],
          currentSortType,
          false // No half-price filter
        ));
      }
    }

    // Finally, update the half price filter state flag
    dispatch({
      type: ACTION_TYPES.TOGGLE_HALF_THE_PRICE_PRODUCTS,
      isHalfThePriceClicked: isHalfThePriceClicked,
    });
  };

// Flag to track active search requests and prevent race conditions
let activeSearchRequest = false;

// Register the active search checker with shopifyClient
registerActiveSearchChecker(() => activeSearchRequest);

export const getItemsToDisplay =
  (
    searchTerm: string,
    categoryFilterId: number,
    itemFilters: any,
    sortType: string,
    isHalfThePriceClicked: boolean
  ) =>
  async (dispatch: Dispatch<any>, getState: () => ReduxState) => {
    try {
      console.log('Starting to fetch products from Shopify...');
      console.log('Search params:', { searchTerm, categoryFilterId, sortType, isHalfThePriceClicked });
      
      // If this is a search request, set the active search flag
      if (searchTerm && searchTerm.trim() !== '') {
        console.log(`Setting activeSearchRequest=true for search: "${searchTerm}"`);
        activeSearchRequest = true;
      }
      
      // Get the current state
      const state = getState();
      
      // Get the selected category node from the state
      const selectedCategory = state.categories.selectedNode;
      
      // Use Shopify client to fetch products
      let shopifyProducts = [];
      let pageInfo = { hasNextPage: false, endCursor: null };
      
      // Set loading state
      dispatch({
        type: ACTION_TYPES.SET_LOADING_STATE,
        isLoading: true
      });
      
      // Convert API sort type to user-friendly name
      let sortTypeName = '';
      if (sortType === 'A-Z') sortTypeName = 'Name (A-Z)';
      if (sortType === 'Z-A') sortTypeName = 'Name (Z-A)';
      if (sortType === 'HighestPrice') sortTypeName = 'Price (High to Low)';
      if (sortType === 'LowestPrice') sortTypeName = 'Price (Low to High)';
      if (sortType === 'Difference') sortTypeName = 'Biggest Savings';
      
      // Helper function to detect if we're on a specific category page by URL
      const isCategoryPage = () => {
        // Check URL path to see if we're on a category page like /browse/drinks
        const path = window.location.pathname;
        // If path has more segments than just /browse, it's a category page
        return path.split('/').filter(p => p).length > 1;
      };

      // Get category slug from URL if we're on a category page
      const getCategorySlugFromURL = () => {
        const path = window.location.pathname;
        const segments = path.split('/').filter(p => p);
        // If we have at least /browse/[category], return the category
        if (segments.length > 1) {
          return segments[1]; // Return the category slug
        }
        return null;
      };

      if (searchTerm && searchTerm.trim() !== '') {
        // Search for products by search term
        const searchResult = await searchProducts(searchTerm);
        shopifyProducts = searchResult.products || [];
        pageInfo = searchResult.pageInfo || { hasNextPage: false, endCursor: null };
      } else if (selectedCategory && selectedCategory.url_slug) {
        // Fetch products by collection handle (category) from Redux state
        console.log(`Fetching products for category: ${selectedCategory.url_slug} from Redux state`);
        const result = await fetchProductsByCollectionHandle(selectedCategory.url_slug);
        shopifyProducts = result.products || [];
        pageInfo = result.pageInfo || { hasNextPage: false, endCursor: null };
      } else if (isCategoryPage()) {
        // We're on a category page but the category isn't in Redux state yet
        // This happens after page refresh - use URL to determine category
        const categorySlug = getCategorySlugFromURL();
        console.log(`Detected category page from URL: ${categorySlug}`);
        
        if (categorySlug) {
          const result = await fetchProductsByCollectionHandle(categorySlug);
          shopifyProducts = result.products || [];
          pageInfo = result.pageInfo || { hasNextPage: false, endCursor: null };
        }
      } else {
        // This is the homepage (no search term or category)
        // Use our specialized function that prioritizes Half Price Homepage collection
        console.log('Homepage detected - using fetchHomepageProducts to prioritize Half Price items');
        const result = await fetchHomepageProducts();
        shopifyProducts = result.products || [];
        pageInfo = result.pageInfo || { hasNextPage: false, endCursor: null };
      }
      
      console.log(`Fetched ${shopifyProducts.length} products from Shopify`);
      
      // Transform Shopify products to the format expected by the app
      console.log('About to transform products:', shopifyProducts.length > 0 ? shopifyProducts[0] : 'No products');
      const transformedProducts = transformShopifyProductsToItemDataArray(shopifyProducts);
      console.log('Transformed products count:', transformedProducts.length);
      console.log('First transformed product:', transformedProducts.length > 0 ? transformedProducts[0] : 'No products');
      
      // Filter out products with $0 price for both vendors
      console.log('Filtering out products with $0 price for both vendors');
      let filteredProducts = transformedProducts.filter(product => {
        // Check if the product has at least one valid price
        if (!product.item_pricing || !Array.isArray(product.item_pricing)) {
          return false;
        }
        return product.item_pricing.some(pricing => pricing.price && pricing.price > 0);
      });
      
      console.log(`After filtering $0 prices: ${filteredProducts.length} products remain`);
      
      // Apply half price filter if selected
      if (isHalfThePriceClicked) {
        console.log('Applying half price filter to fetched products');
        filteredProducts = transformedProducts.filter(product => {
          // Check both the metafield and the direct property for half price items
          const halfPriceMetafield = product.metafields?.find(
            meta => meta.key === 'Is half price or better' || meta.key === 'is_half_price_or_better'
          );
          return !!halfPriceMetafield || product.isHalfThePrice === true;
        });
        console.log(`After half price filter: ${filteredProducts.length} products remain`);
      }
      
      // Apply any additional filters from itemFilters
      // This would depend on your specific filter implementation
      
      // Apply client-side sorting based on the sortType
      // Determine if this is homepage (no search term and no category)
      const isHomepage = !searchTerm && !categoryFilterId;
      
      // If we're on the homepage, preserve the order from fetchHomepageProducts
      // If we're viewing a collection (categoryFilterId exists) AND no sort type is selected,
      // use MANUAL sort to preserve Shopify's collection order
      // If the user has selected a specific sort, respect that choice
      const effectiveSortType = isHomepage ? 'PRESERVE_ORDER' : 
                               (categoryFilterId ? 'MANUAL' : (sortType || 'default'));
      
      // If we're using manual sort or preserving order, make sure it's reflected in UI
      // For better diagnostics, add a console trace that shows both the input and effective sort
      console.log(`Sort info - Input: ${sortType}, Effective: ${effectiveSortType}, Category ID: ${categoryFilterId}, isHomepage: ${isHomepage}`);
      
      // If we're on the homepage and need to preserve order from fetchHomepageProducts, don't sort
      let sortedProducts;
      if (isHomepage && effectiveSortType === 'PRESERVE_ORDER') {
        console.log(`Homepage detected - preserving order of ${filteredProducts.length} products from 'Half Price Homepage' collection`);
        sortedProducts = filteredProducts; // Keep original order
      } else {
        console.log(`Applying sort '${effectiveSortType}' to ${filteredProducts.length} products`);
        sortedProducts = applySortToItems(filteredProducts, effectiveSortType);
      }
      
      // Dispatch the products to the store
      console.log('Dispatching products to store, count:', sortedProducts.length);
      dispatch({
        type: ACTION_TYPES.DISPLAY_ITEMS,
        itemsToDisplay: sortedProducts,
        pageNumber: 1,
        totalPages: pageInfo.hasNextPage ? 2 : 1, // If there are more pages, set totalPages to at least 2
        totalProductsFound: sortedProducts.length,
        // When viewing categories, ensure we save the effectiveSortType to maintain consistency
        // This ensures the Redux store knows we're using MANUAL sort
        sortType: (categoryFilterId && effectiveSortType === 'MANUAL') ? 'MANUAL' : (sortType || "A-Z"),
        // For UI display purposes, use a readable name
        sortTypeName: (categoryFilterId && effectiveSortType === 'MANUAL') ? 'Collection Order' : (sortTypeName || "Name (A-Z)"),
        matchedItem: [],
        closeMatchId: 1,
        currentItemId: 1,
        // Store pagination info for lazy loading
        paginationInfo: {
          hasNextPage: pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor,
          collectionHandle: selectedCategory?.url_slug || null,
          searchTerm: searchTerm || null
        }
      });
      
      // Set loading state to false
      dispatch({
        type: ACTION_TYPES.SET_LOADING_STATE,
        isLoading: false
      });
      
      // If this was a search request, reset the flag now that we're done
      if (searchTerm && searchTerm.trim() !== '') {
        console.log(`Completed search for "${searchTerm}", resetting activeSearchRequest=false`);
        // Add a small delay before clearing the flag to ensure UI has updated
        setTimeout(() => {
          activeSearchRequest = false;
        }, 500);
      }
      
    } catch (error) {
      console.error('Error fetching products:', error);
      
      // Dispatch an error state
      dispatch({
        type: ACTION_TYPES.SET_ERROR_STATE,
        error: "Failed to fetch products. Please try again."
      });
      
      // Set loading state to false
      dispatch({
        type: ACTION_TYPES.SET_LOADING_STATE,
        isLoading: false
      });
      
      // If this was a search request that failed, still reset the flag
      if (searchTerm && searchTerm.trim() !== '') {
        console.log(`Search for "${searchTerm}" failed, resetting activeSearchRequest=false`);
        activeSearchRequest = false;
      }
    }
  };

export const getMoreItems =
  (
    searchTerm: string,
    categoryFilterId: number,
    pageNumber: number,
    currentItemId: number,
    isHalfThePriceClicked?: boolean,
    sortTypeName?: string
  ) =>
  async (dispatch: Dispatch<any>, getState: () => ReduxState) => {
    try {
      console.log('Loading more products...');
      
      // CRITICAL BUGFIX: Check empty half-price batch count first
      if (isHalfThePriceClicked) {
        const state = getState();
        const emptyBatchCount = state.items.emptyHalfPriceBatchCount || 0;
        
        // If we've already had too many empty batches, stop trying
        if (emptyBatchCount >= MAX_EMPTY_HALF_PRICE_BATCHES) {
          console.log(`LIMIT REACHED: Stopping pagination after ${emptyBatchCount}/${MAX_EMPTY_HALF_PRICE_BATCHES} empty half-price batches`);
          
          // CRITICAL FIX: Set hasNextPage to false to prevent any more loading attempts
          dispatch({
            type: ACTION_TYPES.SET_PAGINATION_INFO,
            paginationInfo: {
              hasNextPage: false, // This is critical - tells the system there are no more products
              endCursor: state.items.paginationInfo?.endCursor || null,
              collectionHandle: state.items.paginationInfo?.collectionHandle || null,
              searchTerm: state.items.paginationInfo?.searchTerm || null
            }
          });
          
          return;
        }
      }
      
      // Get the current state
      const state = getState();
      
      // Get pagination info from the state
      const paginationInfo: PaginationInfo = state.items.paginationInfo || {
        hasNextPage: false,
        endCursor: null,
        collectionHandle: null,
        searchTerm: null
      };
      const { endCursor, hasNextPage, collectionHandle } = paginationInfo;
      
      // If there's no next page, return early
      if (!hasNextPage) {
        console.log('No more products to load');
        return;
      }
      
      // Set loading more state
      dispatch({
        type: ACTION_TYPES.SET_LOADING_MORE_STATE,
        isLoadingMore: true
      });
      
      // Use the stored cursor to fetch the next page of products
      let result;
      if (searchTerm && searchTerm.trim() !== '') {
        // Search for more products with the cursor
        result = await searchProducts(searchTerm, endCursor);
      } else if (collectionHandle) {
        // Fetch more products for the collection with the cursor
        result = await fetchProductsByCollectionHandle(collectionHandle, endCursor);
      } else {
        // Fetch more products from all products with the cursor
        result = await fetchAllProducts(endCursor);
      }
      
      const { products = [], pageInfo = { hasNextPage: false, endCursor: null } } = result;
      
      console.log(`Fetched ${products.length} more products`);
      
      // Transform the products to the format expected by the app
      const transformedProducts = transformShopifyProductsToItemDataArray(products);
      
      // Apply half price filter if selected
      let filteredProducts = transformedProducts;
      if (isHalfThePriceClicked) {
        console.log('Applying half price filter to fetched products');
        filteredProducts = transformedProducts.filter(product => {
          // Check both the metafield and the direct property for half price items
          const halfPriceMetafield = product.metafields?.find(
            meta => meta.key === 'Is half price or better' || meta.key === 'is_half_price_or_better'
          );
          return !!halfPriceMetafield || product.isHalfThePrice === true;
        });
        console.log(`After half price filter: ${filteredProducts.length} products remain`);
        
        // CRITICAL BUGFIX: Track empty half-price batches
        const state = getState();
        const currentEmptyBatchCount = state.items.emptyHalfPriceBatchCount || 0;
        
        if (filteredProducts.length === 0) {
          // Increment empty batch count
          const newCount = currentEmptyBatchCount + 1;
          dispatch({
            type: ACTION_TYPES.SET_EMPTY_HALF_PRICE_BATCH_COUNT,
            count: newCount
          });
          console.log(`Empty half-price batch detected! Count now at ${newCount}/${MAX_EMPTY_HALF_PRICE_BATCHES}`);
          
          // Add explicit warning when getting close to the limit
          if (newCount >= MAX_EMPTY_HALF_PRICE_BATCHES - 2 && newCount < MAX_EMPTY_HALF_PRICE_BATCHES) {
            console.log(`APPROACHING LIMIT: Only ${MAX_EMPTY_HALF_PRICE_BATCHES - newCount} more empty batches before stopping.`);
          }
        } else {
          // Reset empty batch count if we found items
          dispatch({
            type: ACTION_TYPES.SET_EMPTY_HALF_PRICE_BATCH_COUNT,
            count: 0
          });
          console.log(`Found ${filteredProducts.length} half-price items! Resetting empty batch counter to 0/${MAX_EMPTY_HALF_PRICE_BATCHES}.`);
        }
      }
      
      // Always apply the current sort to newly loaded items
      // First, get the current sort type from state or from parameter
      const currentSortTypeName = state.items.sortTypeName || sortTypeName || 'Name (A-Z)';
      console.log(`Current sort is: ${currentSortTypeName}`);
      
      // Convert user-friendly sort name to API sort type
      const currentSortType = sortByOrder(currentSortTypeName);
      console.log(`Converted to API sort type: ${currentSortType}`);
      
      // Apply the same sort that's currently being used to the new items
      let sortedProducts = filteredProducts;
      if (currentSortType) {
        console.log(`Applying sort '${currentSortType}' to ${filteredProducts.length} additional products`);
        sortedProducts = applySortToItems(filteredProducts, currentSortType);
      }
      
      // ALWAYS update the pagination info with the new cursor, even if no products were found
      // This is CRITICAL to avoid requesting the same batch of products repeatedly
      dispatch({
        type: ACTION_TYPES.SET_PAGINATION_INFO,
        paginationInfo: {
          hasNextPage: pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor,
          collectionHandle,
          searchTerm: searchTerm && searchTerm.trim() !== '' ? searchTerm : null
        }
      });
      
      // Dispatch the additional products to the store
      console.log(`Dispatching GET_MORE_ITEMS with sort: ${currentSortTypeName} (${currentSortType})`);
      console.log(`Using UPDATED cursor for next fetch: ${pageInfo.endCursor}`);
      dispatch({
        type: ACTION_TYPES.GET_MORE_ITEMS,
        itemsToDisplay: sortedProducts,
        pageNumber: pageNumber + 1,
        currentItemId: currentItemId,
        totalPages: pageInfo.hasNextPage ? pageNumber + 2 : pageNumber + 1,
        totalProductsFound: state.items.totalProductsFound + sortedProducts.length,
        // Critical: EXPLICITLY include sort information for proper re-sorting in the reducer
        sortType: currentSortType || 'A-Z',
        sortTypeName: currentSortTypeName,
        // Update pagination info for next lazy load
        // CRITICAL BUGFIX: Always update pagination info with the new cursor, even with 0 results
        paginationInfo: {
          hasNextPage: isHalfThePriceClicked && filteredProducts.length === 0 ? true : pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor, // This ensures we use the NEW cursor for the next request
          collectionHandle,
          searchTerm: searchTerm || null
        }
      });
      
      // Set loading more state to false
      dispatch({
        type: ACTION_TYPES.SET_LOADING_MORE_STATE,
        isLoadingMore: false
      });
      
    } catch (error) {
      console.error('Error fetching more products:', error);
      
      // Set loading more state to false
      dispatch({
        type: ACTION_TYPES.SET_LOADING_MORE_STATE,
        isLoadingMore: false
      });
      
      // Dispatch an error state
      dispatch({
        type: ACTION_TYPES.SET_ERROR_STATE,
        error: "Failed to load more products. Please try again."
      });
    }
  };

export const closeMatchItem =
  (itemId: number, availableItemId: number) => async () => {
    return await httpClient.instance
      .post("/item/close-match", {
        item_id: itemId,
        available_item_id: availableItemId,
      })
      .then(async (res) => {
        const close_match_id = res.data.id;
        await httpClient.instance
          .patch("/item/close-match", {
            item_id: itemId,
            close_match_id,
          })
          .then((res) => {})
          .catch((e) => {
            console.log(e);
          });
      })
      .catch((e) => {
        console.log(e);
      });
  };

export const updateItemCloseMatchId =
  (itemId: number) => async (dispatch: Dispatch<any>) => {
    return await httpClient.instance
      .patch("/item/close-match", {
        item_id: itemId,
      })
      .then((res) => {
        // console.log("updated the item table");
      })
      .catch((e) => {
        console.log(e);
      });
  };

export async function getItemCloseMatchId(itemId: number) {
  const item_id = itemId;
  return await httpClient.instance
    .get("/item/close-match?id=" + item_id)
    .then((res) => {
      return res.data;
    });
}

export const checkItemCloseMatch = async (itemData: ItemData) => {
  const itemName = itemData.name;
  const vendorId = itemData.selected_vendor_id === 1 ? 2 : 1;

  if (
    itemName.includes("Coles") ||
    itemName.includes("coles") ||
    itemName.includes("Woolworths") ||
    itemName.includes("woolworths")
  ) {
    // build the query structure
    const searchPathname = getItemsApiQueryPathname(itemName, 0, vendorId);

    // call the api
    await axios
      .get(process.env.REACT_APP_BACKEND_URL + searchPathname)
      .then((res) => {
        const itemsArray: ItemData[] = res.data;
        const data = itemsArray.filter(
          (item) =>
            item.item_pricing.length === 1 &&
            (item.name.includes("Coles") || item.name.includes("Woolworths"))
        );
        return data;
      })
      .catch((err) => {
        console.log(err);
      });
  } else {
    return null;
  }
};

function getItemsApiQueryPathname(
  searchTerm?: string,
  categoryFilterId?: number,
  vendorId?: number,
  isHalfThePrice?: boolean | undefined,
  sortBy?: string
) {
  // build the query structure
  let searchPathname = "";

  // use the search endpoint
  searchPathname += "/search?";

  if (searchTerm && searchTerm.length) {
    if (vendorId) {
      searchPathname +=
        "q=" + searchTerm + "&vendor_id= " + vendorId + "&limit=1&";
    } else {
      searchPathname += "q=" + searchTerm + "&";
    }
  }

  if (categoryFilterId) {
    searchPathname += `catId=${categoryFilterId}&`;
  }

  if (isHalfThePrice) {
    searchPathname += "isHalfThePrice=1&";
  }

  if (sortBy) {
    searchPathname += `sortBy=${sortBy}`;
  }
  return searchPathname;
}

const sortByOrder = (sortTypeName?: string) => {
  let sortBy = "";

  if (!sortTypeName) return sortBy;

  if (sortTypeName === "Name (A-Z)") {
    sortBy = "A-Z";
  }

  if (sortTypeName === "Name (Z-A)") {
    sortBy = "Z-A";
  }

  if (sortTypeName === "Price (High to Low)") {
    sortBy = "HighestPrice";
  }

  if (sortTypeName === "Price (Low to High)") {
    sortBy = "LowestPrice";
  }

  if (sortTypeName === "Biggest Savings") {
    sortBy = "Difference";
  }

  return sortBy;
};
